/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type {
  AiQueryLauncher,
  AiQueryOidcConfig,
  AiQueryOidcEnv,
  CozeChatQuery,
} from './types';
import {
  AI_QUERY_OIDC_DEFAULT_RETURN_PATH,
  COZE_CHAT_ENTRY_PATH,
} from './constants';

function trimEnv(value: string | undefined): string {
  return value?.trim() || '';
}

function parseUrlOrigin(url: string): string | undefined {
  try {
    return new URL(url).origin;
  } catch {
    return undefined;
  }
}

function resolveIdpOrigin(env: AiQueryOidcEnv): string {
  const bridgeUrl = trimEnv(env.VITE_OIDC_IDP_BRIDGE_URL);
  if (bridgeUrl) {
    const origin = parseUrlOrigin(bridgeUrl);
    if (origin) {
      return origin;
    }
  }
  const origin = trimEnv(env.VITE_OIDC_IDP_ORIGIN);
  return origin ? origin.replace(/\/$/, '') : '';
}

function resolveAppOrigin(env: AiQueryOidcEnv): string {
  const abrUrl = trimEnv(env.VITE_ABR_WEBCHAT_URL);
  const legacyUrl = trimEnv(env.VITE_AI_QUERY_APP_URL);
  return (abrUrl || legacyUrl).replace(/\/$/, '');
}

function appendBridgeTimestamp(url: URL): string {
  url.searchParams.set('_tology_bridge', String(Date.now()));
  return url.href;
}

function buildRpOidcReturnUrl(
  appOrigin: string,
  returnPathRaw: string,
): string {
  let pathOnly =
    returnPathRaw.split('?')[0]?.trim() || AI_QUERY_OIDC_DEFAULT_RETURN_PATH;
  if (pathOnly.endsWith('/relogin')) {
    pathOnly = pathOnly.replace(/\/relogin$/, '/login');
  }
  const returnPath = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
  const url = new URL(returnPath, appOrigin);
  return appendBridgeTimestamp(url);
}

/** 由 env 解析 OIDC 配置，可供其他项目传入自己的 env 对象复用 */
export function resolveAiQueryOidcConfig(
  env: AiQueryOidcEnv,
): AiQueryOidcConfig {
  const idpOrigin = resolveIdpOrigin(env);
  const launcherPath =
    trimEnv(env.VITE_OIDC_BRIDGE_LAUNCHER_PATH) || '/tology-bridge.html';
  const appOrigin = resolveAppOrigin(env);
  const returnPathRaw = (
    trimEnv(env.VITE_AI_QUERY_OIDC_RETURN_PATH) ||
    AI_QUERY_OIDC_DEFAULT_RETURN_PATH
  ).trim();

  return {
    idpOrigin,
    launcherPath: launcherPath.startsWith('/')
      ? launcherPath
      : `/${launcherPath}`,
    appOrigin,
    returnTo: buildRpOidcReturnUrl(appOrigin, returnPathRaw),
    appHomeUrl: `${appOrigin}/`,
  };
}

export function buildAiQueryLauncher(
  config: AiQueryOidcConfig,
): AiQueryLauncher {
  const launcherUrl = new URL(config.launcherPath, config.idpOrigin);
  launcherUrl.searchParams.set('return_to', config.returnTo);
  return {
    href: launcherUrl.href,
    idpOrigin: config.idpOrigin,
    config,
  };
}

export function resolveAiQueryLauncherFromEnv(
  env: AiQueryOidcEnv,
): AiQueryLauncher {
  return buildAiQueryLauncher(resolveAiQueryOidcConfig(env));
}

/** 独立分享 / 直连打开：abr-webchat `/chat/agent` */
export function buildCozeChatReturnTo(
  appOrigin: string,
  query: CozeChatQuery,
): string {
  const url = new URL(COZE_CHAT_ENTRY_PATH, appOrigin);
  appendCozeChatQueryParams(url, query);
  return appendBridgeTimestamp(url);
}

function appendCozeChatQueryParams(url: URL, query: CozeChatQuery): void {
  url.searchParams.set('bot_id', query.bot_id);
  url.searchParams.set('space_id', query.space_id);
  url.searchParams.set('scene', query.scene);
  url.searchParams.set('entry', query.entry ?? 'embed');
  if (query.connector_id) {
    url.searchParams.set('connector_id', query.connector_id);
  }
  if (query.inference_mode) {
    url.searchParams.set('inference_mode', query.inference_mode);
  }
  if (query.bot_name) {
    url.searchParams.set('bot_name', query.bot_name);
  }
  if (query.force_reset) {
    url.searchParams.set('force_reset', '1');
  }
}

/**
 * 嵌入 OIDC return_to：路径与 tology 相同（默认 /oauth/oidc/login），仅追加 Coze query。
 */
export function buildCozeEmbedReturnTo(
  appOrigin: string,
  query: CozeChatQuery,
  returnPathRaw: string,
): string {
  let pathOnly =
    returnPathRaw.split('?')[0]?.trim() || AI_QUERY_OIDC_DEFAULT_RETURN_PATH;
  if (pathOnly.endsWith('/relogin')) {
    pathOnly = pathOnly.replace(/\/relogin$/, '/login');
  }
  const returnPath = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
  const url = new URL(returnPath, appOrigin);
  appendCozeChatQueryParams(url, query);
  return appendBridgeTimestamp(url);
}

export function buildCozeAiQueryLauncher(
  env: AiQueryOidcEnv,
  query: CozeChatQuery,
): AiQueryLauncher {
  const baseConfig = resolveAiQueryOidcConfig(env);
  const returnPathRaw = (
    trimEnv(env.VITE_AI_QUERY_OIDC_RETURN_PATH) ||
    AI_QUERY_OIDC_DEFAULT_RETURN_PATH
  ).trim();
  const config: AiQueryOidcConfig = {
    ...baseConfig,
    returnTo: buildCozeEmbedReturnTo(
      baseConfig.appOrigin,
      query,
      returnPathRaw,
    ),
  };
  return buildAiQueryLauncher(config);
}

/** 校验配置是否可用于打开/嵌入智能查询 */
export function validateAiQueryOidcConfig(
  config: AiQueryOidcConfig,
): { ok: true } | { ok: false; reason: 'missing_idp' | 'missing_app' } {
  if (!config.idpOrigin) {
    return { ok: false, reason: 'missing_idp' };
  }
  if (!config.appOrigin) {
    return { ok: false, reason: 'missing_app' };
  }
  return { ok: true };
}

function pickViteEnv(raw: unknown): string | undefined {
  if (typeof raw !== 'string' || !raw.trim()) {
    return undefined;
  }
  return raw.trim();
}

export function isAbrWebchatDirectEmbedEnabled(env?: AiQueryOidcEnv): boolean {
  const raw = trimEnv(
    env?.VITE_ABR_WEBCHAT_DIRECT_EMBED ??
      pickViteEnv(import.meta.env.VITE_ABR_WEBCHAT_DIRECT_EMBED),
  );
  return raw === 'true' || raw === '1';
}

export function getCozeEmbedEnv(): AiQueryOidcEnv {
  // 须静态访问各字段，Rsbuild define 才能在编译期替换（见 coze-studio/rsbuild.config.ts）
  return {
    VITE_OIDC_IDP_BRIDGE_URL: pickViteEnv(
      import.meta.env.VITE_OIDC_IDP_BRIDGE_URL,
    ),
    VITE_OIDC_IDP_ORIGIN: pickViteEnv(import.meta.env.VITE_OIDC_IDP_ORIGIN),
    VITE_OIDC_BRIDGE_LAUNCHER_PATH: pickViteEnv(
      import.meta.env.VITE_OIDC_BRIDGE_LAUNCHER_PATH,
    ),
    VITE_ABR_WEBCHAT_URL: pickViteEnv(import.meta.env.VITE_ABR_WEBCHAT_URL),
    VITE_AI_QUERY_APP_URL: pickViteEnv(import.meta.env.VITE_AI_QUERY_APP_URL),
    VITE_AI_QUERY_OIDC_RETURN_PATH: pickViteEnv(
      import.meta.env.VITE_AI_QUERY_OIDC_RETURN_PATH,
    ),
    VITE_ABR_WEBCHAT_DIRECT_EMBED: pickViteEnv(
      import.meta.env.VITE_ABR_WEBCHAT_DIRECT_EMBED,
    ),
  };
}
